import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Button, Dropdown, Form, Table } from 'react-bootstrap'
import { ArrowLeft, Pencil } from 'react-bootstrap-icons'
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { toast, ToastContainer } from 'react-toastify'

import classNames from 'classnames'

import Badge from '../../components/_atoms/Badge/Badge'
import CategoryIcon from '../../components/_atoms/CategogyIcon/CategoryIcon'
import CellEditPrice from '../../components/_atoms/CellEditPrice/CellEditPrice'
import CopyButton from '../../components/_atoms/CopyButton/CopyButton'
import HoverOverlay from '../../components/_atoms/HoverOverlay/HoverOverlay'
import ListNoItems from '../../components/_atoms/ListNoItems/ListNoItems'
import ListPagination from '../../components/_atoms/ListPagination/ListPagination'
import ListSkeleton from '../../components/_atoms/ListSkeleton/ListSkeleton'
import SalesText from '../../components/_atoms/SalesText/SalesText'
import TableHeadPartSearch from '../../components/_atoms/TableHeadPartSearch/TableHeadPartSearch'
import TableHeadRangeFilter from '../../components/_atoms/TableHeadRangeFilter/TableHeadRangeFilter'
import ColumnSort from '../../components/_molecules/ColumnSort/ColumnSort'
import { ModalPartsBulkApply } from '../../components/_molecules/ModalPartsBulkActions/ModalPartsBulkApply'
import { ModalPartsBulkConfirm } from '../../components/_molecules/ModalPartsBulkActions/ModalPartsBulkConfirm'
import NavigationButtons from '../../components/_molecules/NavigationButtons/NavigationButtons'
import SearchComponent from '../../components/_molecules/SearchComponent/SearchComponent'
import TableHeadCategorySearchParts from '../../components/_molecules/TableHeadCategorySearchParts/TableHeadCategorySearchParts'
import TableHeadMarkSearchParts from '../../components/_molecules/TableHeadMarkSearchParts/TableHeadMarkSearchParts'
import TableHeadModelSearchParts from '../../components/_molecules/TableHeadModelSearchParts/TableHeadModelSearchParts'
import TableHeadPartNumberSearchParts from '../../components/_molecules/TableHeadPartNumberSearchParts/TableHeadPartNumberSearchParts'
import CellThumbCarousel from '../../components/_orgamisms/CellThumbCarousel/CellThumbCarousel'
import { pages } from '../../constants/parts/navigationButtonsPages'
import { fontSizes, fontWeights } from '../../constants/salesStyles'
import useResetFiltersOnUnmount from '../../hooks/partList/useResetFiltersOnUnmount'
import loadPartList from '../../modules/redux/partList/LoadPartList'
import setPartArchivedStatus from '../../modules/redux/partList/setPartArchivedStatus'
import { useDispatch } from '../../modules/store/customDispatch'
import { useSelector } from '../../modules/store/customSelector'
import { useSetPartsArchivedStatusHandlerMutation } from '../../modules/store/datahubApi/datahubApi'
import { storeDispatchTypes } from '../../modules/store/storeDispatchTypes'
import { adminRouteAlias, getAdminNavigationPath } from '../../router/adminRouteAlias'
import { SortFieldParts, SortIconType } from '../../types/common/commonTypes'
import { EDocumentType } from '../../types/document/documentTypes'
import { Part, PartListSearchParams } from '../../types/part/partTypes'
import { countDaysInStock, formatDate, num_word } from '../../utils/converters'
import { colWidth } from '../../utils/stylePatches'
import DatahubApiService from '../../modules/api/DatahubApiService'
import ModalEditPart from '../../components/_molecules/ModalEditPart/ModalEditPart'

import styles from './styles.module.scss'

const ArchivedPartList = () => {
    const params = useParams<{ partId: string }>()
    const location = useLocation()
    const dispatch = useDispatch()
    const [perPage] = useState(50)

    const navigate = useNavigate()
    let pagesParts = pages

    const isLoading = useSelector(state => state.partList.isLoading.partList)
    const parts = useSelector(state => state.partList.partList.data)
    const currentPart = useSelector(state => state.partList.currentPart)

    // Sort
    const sortOrder = useSelector(state => state.partList.sortOrder)

    // Car filter
    const carFilter = useSelector(state => state.partList.filters.car)

    //Select search filter
    const modelId = useSelector(state => state.partList.filters.modelId)
    const markId = useSelector(state => state.partList.filters.markId)
    const categoryId = useSelector(state => state.partList.filters.categoryId)
    const partId = useSelector(state => state.partList.filters.partId)
    const partNumber = useSelector(state => state.partList.filters.partNumber)

    // search filter part title
    const partName = useSelector(state => state.partList.filters.partName)

    // Range Filters
    const priceRange = useSelector(state => state.partList.filters.priceRange)
    const priceFrom = useSelector(state => state.partList.filters.priceRange?.from)
    const priceTo = useSelector(state => state.partList.filters.priceRange?.to)

    const yearRange = useSelector(state => state.partList.filters.yearRange)
    const yearFrom = useSelector(state => state.partList.filters.yearRange?.from)
    const yearTo = useSelector(state => state.partList.filters.yearRange?.to)
    const page = useSelector(state => state.partList.filters.page)

    // Counts
    const [counts, setCounts] = useState<Record<string, number>>()
    const itemsCount = useSelector(state => state.partList.partList.dataCount)
    const itemsTotal = useSelector(state => state.partList.partList.total)
    const draftCount = useSelector(state => state.partList.partList.draftCount)
    const archivedCount = useSelector(state => state.partList.partList.archivedCount)
    const soldCount = useSelector(state => state.partList.partList.soldCount)

    useEffect(() => {
        if (!isLoading) {
            setCounts({
                itemsTotal,
                draftCount,
                archivedCount,
                soldCount,
            })
        }
    }, [isLoading, itemsTotal, draftCount, archivedCount, soldCount])

    useEffect(() => {
        pagesParts = pagesParts.map(page => ({
            ...page,
            count: counts && counts[page.countKey],
        }))
    }, [counts])

    // Параметры поиска
    const [partListSearchParams, setPartListSearchParams] = useState<PartListSearchParams>({})
    const [searchParams, setSearchParams] = useSearchParams()
    const [searchParamsLoaded, setSearchParamsLoaded] = useState(0)
    const searchParamCarId = searchParams.get('carId')
    const searchParamPage = searchParams.get('p')
    const searchParamPartName = searchParams.get('pn')
    const searchParamCategoryId = searchParams.get('g')
    const searchParamMarkId = searchParams.get('markId')
    const searchParamModelId = searchParams.get('modelId')
    const searchParamPriceFrom = searchParams.get('priceFrom')
    const searchParamPriceTo = searchParams.get('priceTo')
    const searchParamPartNumber = searchParams.get('partNumber')
    const searchParamSortOrder = searchParams.get('sortOrder')

    const filterPartsByPartName = (partName: string) => {
        dispatch({
            type: storeDispatchTypes.setPartNameTextFilterParts,
            value: {
                partName: partName,
            },
        })
    }

    const filterPartsByCategoryId = (categoryId: string) => {
        dispatch({
            type: storeDispatchTypes.setCategoryFilterParts,
            value: {
                filterId: categoryId,
            },
        })
    }

    const filterPartsByMarkId = (markId: string) => {
        dispatch({
            type: storeDispatchTypes.setMarkFilterParts,
            value: {
                filterId: markId,
            },
        })
    }

    const filterPartsByModelId = (modelId: string) => {
        dispatch({
            type: storeDispatchTypes.setModelFilterParts,
            value: {
                filterId: modelId,
            },
        })
    }

    const filterPartsByPriceRange = (priceFrom: string | null, priceTo: string | null) => {
        dispatch({
            type: storeDispatchTypes.setPriceRangeParts,
            value: {
                from: priceFrom,
                to: priceTo,
            },
        })
    }

    const filterPartsByPartNumber = (partNumber: string) => {
        dispatch({
            type: storeDispatchTypes.setPartNumberFilterParts,
            value: {
                filterId: partNumber,
            },
        })
    }

    const filterPartsBySortOrder = (sortField: string, sortOrder: string) => {
        dispatch({
            type: storeDispatchTypes.setPartListSort,
            value: {
                sortField,
                sortOrder,
            },
        })
    }

    // Загрузка параметров из строки поиска
    useEffect(() => {
        if (searchParamPage !== `${page}` && (searchParamPage !== null || page !== 1)) {
            dispatch({
                type: storeDispatchTypes.setPageFilterParts,
                value: { page: Number(searchParamPage) > 1 ? Number(searchParamPage) : 1 },
            })
        }

        if (searchParamPartName !== partName && (searchParamPartName || partName !== undefined)) {
            filterPartsByPartName(searchParamPartName ? searchParamPartName : '')
        }

        if (searchParamCategoryId !== `${categoryId}` && (searchParamCategoryId || categoryId !== undefined)) {
            filterPartsByCategoryId(searchParamCategoryId ? searchParamCategoryId : '')
        }

        if (searchParamMarkId !== `${markId}` && (searchParamMarkId || markId !== undefined)) {
            filterPartsByMarkId(searchParamMarkId ? searchParamMarkId : '')
        }

        if (searchParamModelId !== `${modelId}` && (searchParamModelId || modelId !== undefined)) {
            filterPartsByModelId(searchParamModelId ? searchParamModelId : '')
        }

        if (
            searchParamPriceFrom !== `${priceFrom}` ||
            (searchParamPriceFrom === null && priceFrom !== undefined) ||
            searchParamPriceTo !== `${priceTo}` ||
            (searchParamPriceTo === null && priceTo !== undefined)
        ) {
            if (searchParamPriceFrom || priceFrom !== undefined || searchParamPriceTo || priceTo !== undefined) {
                filterPartsByPriceRange(searchParamPriceFrom, searchParamPriceTo)
            }
        }

        if (searchParamPartNumber !== `${partNumber}` && (searchParamPartNumber || partNumber !== undefined)) {
            filterPartsByPartNumber(searchParamPartNumber ? searchParamPartNumber : '')
        }

        if (searchParamSortOrder && searchParamSortOrder !== sortOrder.map(pair => pair.join(',')).join(',')) {
            const searchParamsSet = new Set(searchParamSortOrder.match(/[^,]+,[^,]+/g))
            const sortOrderSet = new Set(sortOrder.map(pair => pair.join(',')))

            // Для каждой пары из sortOrder, которая не содержится в searchParamSortOrder
            sortOrder.forEach(pair => {
                const pairString = pair.join(',')
                if (!searchParamsSet.has(pairString)) {
                    filterPartsBySortOrder(pair[0], 'NONE')
                }
            })

            // Для каждой пары из searchParamSortOrder, которая не содержится в sortOrder
            searchParamsSet.forEach(param => {
                if (!sortOrderSet.has(param)) {
                    const [field, order] = param.split(',')
                    filterPartsBySortOrder(field, order)
                }
            })
        } else if (!searchParamSortOrder) {
            for (const field of sortOrder) {
                filterPartsBySortOrder(field[0], 'NONE')
            }
        }

        if (searchParamCarId !== `${carFilter?.carId[0]}`) {
            if (!searchParamCarId) {
                dispatch({ type: storeDispatchTypes.resetCarFilterParts })
            } else {
                dispatch({
                    type: storeDispatchTypes.setCarFilterParts,
                    value: {
                        car: {
                            carId: [searchParamCarId],
                        },
                    },
                })
            }
        } else if (!carFilter?.carId[0]) {
            dispatch({ type: storeDispatchTypes.resetCarFilterParts })
        }

        setSearchParamsLoaded(1)
    }, [searchParams])

    const loadParts = useCallback(async () => {
        const partListSearchParams: PartListSearchParams = {
            carId: carFilter?.carId,
            modelId,
            markId,
            groupId: categoryId,
            search: partName,
            priceRange: priceRange && { from: priceFrom, to: priceTo },
            yearRange: yearRange && { from: yearFrom, to: yearTo },
            partId,
            partNumber,
            isArchived: true,
        }

        // load parts list data
        dispatch(
            loadPartList({
                ...partListSearchParams,
                pagination: {
                    page,
                    perPage,
                },
                order: sortOrder,
            }),
        )

        setPartListSearchParams(partListSearchParams)
    }, [
        carFilter?.carId,
        categoryId,
        dispatch,
        markId,
        modelId,
        page,
        partId,
        partName,
        partNumber,
        perPage,
        priceFrom,
        priceRange,
        priceTo,
        sortOrder,
        yearFrom,
        yearRange,
        yearTo,
    ])

    // Загрузка параметров в строку поиска
    useEffect(() => {
        if (!searchParamsLoaded) {
            return
        }

        // Текущие параметры
        const currentPage = page > 1 ? `${page}` : null
        const currentCarId = carFilter?.carId ? `${carFilter.carId}` : null
        const currentPartName = partName ? `${partName}` : null
        const currentCategoryId = categoryId ? `${categoryId}` : null
        const currentMarkId = markId ? `${markId}` : null
        const currentModelId = modelId ? `${modelId}` : null
        const currentPriceFrom = priceFrom ? `${priceFrom}` : null
        const currentPriceTo = priceTo ? `${priceTo}` : null
        const currentPartNumber = partNumber ? `${partNumber}` : null
        const currentSortOrder = sortOrder.length ? sortOrder : null

        // Проверяем, изменились ли параметры строки поиска
        const pageChanged = searchParamPage !== currentPage
        const carIdChanged = searchParamCarId !== currentCarId
        const partNameChanged = searchParamPartName !== currentPartName
        const categoryIdChanged = searchParamCategoryId !== currentCategoryId
        const markIdChanged = searchParamMarkId !== currentMarkId
        const modelIdChanged = searchParamModelId !== currentModelId
        const priceFromChanged = searchParamPriceFrom !== currentPriceFrom
        const priceToChanged = searchParamPriceTo !== currentPriceTo
        const partNumberChanged = searchParamPartNumber !== currentPartNumber

        let sortOrderChanged = false
        if (searchParamSortOrder) {
            const searchParamsSet = new Set(searchParamSortOrder.match(/[^,]+,[^,]+/g))
            const sortOrderSet = new Set(sortOrder.map(pair => pair.join(',')))

            sortOrder.forEach(pair => {
                const pairString = pair.join(',')
                if (!searchParamsSet.has(pairString)) {
                    sortOrderChanged = true
                    return
                }
            })

            searchParamsSet.forEach(param => {
                if (!sortOrderSet.has(param)) {
                    sortOrderChanged = true
                    return
                }
            })
        } else if (!searchParamSortOrder && currentSortOrder) {
            sortOrderChanged = true
        }

        const searchParamsChanged =
            carIdChanged ||
            pageChanged ||
            partNameChanged ||
            categoryIdChanged ||
            markIdChanged ||
            modelIdChanged ||
            priceFromChanged ||
            priceToChanged ||
            partNumberChanged ||
            sortOrderChanged

        if (searchParamsChanged) {
            setSearchParams({
                ...(carFilter?.carId.length ? { carId: carFilter?.carId[0] || '' } : {}),
                ...(page > 1 ? { p: '' + page } : {}),
                ...(partName ? { pn: '' + partName } : {}),
                ...(categoryId ? { g: '' + categoryId } : {}),
                ...(markId ? { markId: '' + markId } : {}),
                ...(modelId ? { modelId: '' + modelId } : {}),
                ...(priceFrom ? { priceFrom: '' + priceFrom } : {}),
                ...(priceTo ? { priceTo: '' + priceTo } : {}),
                ...(partNumber ? { partNumber: '' + partNumber } : {}),
                ...(sortOrder && sortOrder.length !== 0 ? { sortOrder: '' + sortOrder } : {}),
            })
        }

        loadParts()
    }, [
        searchParamsLoaded,
        page,
        perPage,
        sortOrder,
        carFilter?.carId,
        modelId,
        markId,
        categoryId,
        partName,
        priceFrom,
        priceTo,
        yearFrom,
        yearTo,
        partId,
        partNumber,
    ])

    const filteredParts = useMemo(
        () =>
            parts.filter(part => {
                const { isArchived } = part
                return isArchived
            }),
        [parts],
    )

    // Главный поиск
    const setNameSearch = (searchTerm: string) => {
        dispatch({
            type: storeDispatchTypes.setPartNameTextFilterParts,
            value: {
                partName: searchTerm,
            },
        })
    }

    const [setPartsArchivedStatusHandler] = useSetPartsArchivedStatusHandlerMutation()

    const handleSetPartsArchivedStatus = (parts?: Part[]) => {
        const partsId = parts?.map(part => part.id)
        setPartsArchivedStatusHandler({
            partsId: parts?.length ? partsId : undefined,
            newArchivedStatus: false,
            params: !parts?.length ? partListSearchParams : undefined,
        })
            .unwrap()
            .then(response => {
                if (response.status === 'ok') {
                    loadParts()

                    toast.success('Запчасти успешно возвращены из архива')
                } else {
                    toast.error('Ошибка возврата из архива')
                }
            })

        handleAllCheckboxCancel()
    }

    // Промежуточная модалка-подтверждение
    const [isApplyActionModalOpen, setIsApplyActionModalOpen] = useState(false)
    const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false)
    const [pendingAction, setPendingAction] = useState<null | ((parts?: Part[]) => void)>(null)
    const [isDisableFilterButton, setIsDisableFilterButton] = useState(false)
    const [modalParts, setModalParts] = useState<Part[]>([])

    const handleActionWithScopeSelection = (action: (parts?: Part[]) => void, disableFilterButton: boolean = false) => {
        setPendingAction(() => action)

        if (!selectAll) {
            setModalParts(selectedParts)
            setIsConfirmModalOpen(true)
            return
        }

        setIsDisableFilterButton(disableFilterButton)
        setIsApplyActionModalOpen(true)
    }

    const handleApplyForPage = () => {
        setModalParts(parts)
        setIsApplyActionModalOpen(false)
        setIsConfirmModalOpen(true)
    }

    const handleApplyForFilter = () => {
        setModalParts([])
        setIsApplyActionModalOpen(false)
        setIsConfirmModalOpen(true)
    }

    const handleConfirmAction = () => {
        if (pendingAction) pendingAction(modalParts)
        setIsConfirmModalOpen(false)
    }

    // Выбранные запчасти в чекбоксах
    const [checksFromModalSellParts, setChecksFromModalSellParts] = useState<{
        documentType: EDocumentType[]
        marketplace?: string
        deal?: any
    }>({
        documentType: [],
        marketplace: '',
        deal: {},
    })
    const selectAll = useSelector(state => state.partList.selectedParts.selectAll)
    const selectedParts = useSelector(state => state.partList.selectedParts.parts)

    const handleCheckboxChange = (part: Part) => {
        const newCheckedParts = [...selectedParts]
        const partIndex = newCheckedParts.findIndex(p => p.id === part.id)

        if (partIndex !== -1) {
            newCheckedParts.splice(partIndex, 1)
        } else {
            newCheckedParts.push(part)
        }

        dispatch({
            type: storeDispatchTypes.setSelectedParts,
            value: newCheckedParts,
        })
    }

    const handleAllCheckboxCancel = () => {
        setModalParts([])
        dispatch({
            type: storeDispatchTypes.setSelectAllParts,
            value: false,
        })
    }

    const handleAllCheckboxChange = () => {
        const newSelectAll = !selectAll
        dispatch({
            type: storeDispatchTypes.setSelectAllParts,
            value: newSelectAll,
        })
    }

    // Сброс фильтров при уходе со страницы
    useResetFiltersOnUnmount()

    // Edit modal flow
    const [isEditModalOpen, setIsEditModalOpen] = useState(false)
    const navigateWithParams = (partId = '') => {
        const queryParams = new URLSearchParams(location.search)
        if (partId) {
            if (params.partId !== partId) {
                navigate(`/admin/${adminRouteAlias.archivedParts.location}/${partId}?${queryParams.toString()}`)
            }
        } else {
            navigate(`/admin/${adminRouteAlias.archivedParts.location}?${queryParams.toString()}`)
        }
    }

    const openEditModal = (part: Part) => {
        setIsEditModalOpen(true)
        navigateWithParams(part.id)
        dispatch({
            type: storeDispatchTypes.setCurrentPart,
            value: part,
        })
    }

    const handleCloseEditModal = () => {
        setIsEditModalOpen(false)
        navigateWithParams()
        dispatch({
            type: storeDispatchTypes.setCurrentPart,
            value: undefined,
        })
    }

    const getPartById = useCallback(
        async (partId: string) => {
            if (!partId) return
            const part = await DatahubApiService.getPartByPartId(partId)
            if (!part) return
            dispatch({ type: storeDispatchTypes.setCurrentPart, value: part })
            setIsEditModalOpen(true)
        },
        [dispatch],
    )

    useEffect(() => {
        if (params.partId) {
            getPartById(params.partId)
        } else {
            setIsEditModalOpen(false)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [params.partId])

    return (
        <div className={styles.wrap}>
            <ToastContainer position="bottom-center" autoClose={5000} />
            {/* Модалки */}
            {isApplyActionModalOpen && (
                <ModalPartsBulkApply
                    isOpen={isApplyActionModalOpen}
                    onClose={() => setIsApplyActionModalOpen(false)}
                    onApplyForPage={handleApplyForPage}
                    onApplyForFilter={handleApplyForFilter}
                    disableFilterButton={isDisableFilterButton}
                />
            )}
            {isConfirmModalOpen && (
                <ModalPartsBulkConfirm
                    isOpen={isConfirmModalOpen}
                    onClose={() => {
                        handleAllCheckboxCancel()
                        setIsConfirmModalOpen(false)
                    }}
                    onConfirm={handleConfirmAction}
                    partsCount={selectAll ? modalParts.length || itemsCount : selectedParts.length}
                />
            )}

            <div className={styles.rowWrap}>
                <SalesText
                    text={'Запчасти в архиве'}
                    fontWeight={fontWeights.bold}
                    fontSize={fontSizes.xxl}
                    letterSpacing={-0.02}
                />
            </div>
            <div className={styles.rowWrap}>
                <SearchComponent oldSearchTerm={partName} setSearchTerm={setNameSearch} />
            </div>
            <div className={styles.rowWrap}>
                <Dropdown className={styles.customDropdown}>
                    <Dropdown.Toggle disabled={!parts.length || (selectedParts.length == 0 && !selectAll)}>
                        Массовые действия
                        {!!(selectAll || selectedParts.length) && (
                            <span style={{ marginLeft: '8px' }}>{selectAll ? itemsCount : selectedParts.length}</span>
                        )}
                    </Dropdown.Toggle>
                    <Dropdown.Menu>
                        <Dropdown.Item
                            onClick={() => {
                                handleActionWithScopeSelection(handleSetPartsArchivedStatus)
                            }}
                        >
                            Возврат из архива
                        </Dropdown.Item>
                    </Dropdown.Menu>
                </Dropdown>
                <NavigationButtons
                    pages={pagesParts}
                    currentPage={getAdminNavigationPath(adminRouteAlias.archivedParts.location)}
                    counts={counts}
                    navigate={navigate}
                />
                {!isLoading && parts.length === 0 ? (
                    <></>
                ) : (
                    <div className={styles.wrapRight}>
                        <ListPagination
                            page={page}
                            setPage={page =>
                                dispatch({
                                    type: storeDispatchTypes.setPageFilterParts,
                                    value: { page },
                                })
                            }
                            perPage={perPage}
                            itemsCount={itemsCount}
                        />
                    </div>
                )}
            </div>
            {currentPart && isEditModalOpen && (
                <ModalEditPart
                    part={currentPart}
                    isOpen={isEditModalOpen}
                    closeModal={handleCloseEditModal}
                    confirmChanges={() => {
                        handleAllCheckboxCancel()
                        loadParts()
                    }}
                />
            )}
            <Table striped hover>
                <thead>
                    <tr>
                        <th scope="col" style={colWidth(35)} className={styles.alignCenter}>
                            {selectAll !== undefined && (
                                <Form.Check>
                                    <Form.Check.Input
                                        className={styles.checkboxLg}
                                        checked={selectAll}
                                        onChange={handleAllCheckboxChange}
                                    />
                                </Form.Check>
                            )}
                        </th>
                        <th scope="col" style={colWidth(54)}>
                            <TableHeadCategorySearchParts />
                        </th>
                        <th scope="col" style={colWidth(204)}>
                            <TableHeadPartSearch oldSearchTerm={partName} />
                        </th>
                        <th scope="col" style={colWidth(100)} className={`${carFilter ? styles.filtered : ''}`}>
                            {carFilter ? carFilter.markTitle : <TableHeadMarkSearchParts />}
                        </th>
                        <th scope="col" style={colWidth(200)} className={`${carFilter ? styles.filtered : ''}`}>
                            {carFilter ? carFilter.modelTitle : <TableHeadModelSearchParts />}
                        </th>
                        <th scope="col" style={colWidth(35)}>
                            Фото
                        </th>
                        <th scope="col" style={colWidth(103)}>
                            <TableHeadPartNumberSearchParts />
                        </th>
                        <th scope="col" style={colWidth(95)}>
                            <TableHeadRangeFilter
                                initialTableHead={'Цена'}
                                step={1000}
                                leftInPixes={-35}
                                initialFromVal={priceFrom}
                                initialToVal={priceTo}
                                widthInPixels={150}
                                storeActionSetRange={storeDispatchTypes.setPriceRangeParts}
                                isColumnSort
                            />
                        </th>
                        <th scope="col" style={colWidth(85)}>
                            Дата
                            <ColumnSort
                                iconType={SortIconType.NUMERIC}
                                sortField={SortFieldParts.UPDATED_AT}
                                slice={'partList'}
                                reverseSort={true}
                            />
                        </th>
                        <th scope="col" style={colWidth(99)}>
                            Действия
                        </th>
                    </tr>
                </thead>
                {isLoading || !parts ? (
                    <ListSkeleton rowCount={9} columnCount={10} marginVerticalRem={0.21} />
                ) : (
                    <tbody>
                        {filteredParts.map((part, index) => (
                            <tr key={index} className={styles.row}>
                                <td scope="row" className={styles.alignCenter}>
                                    <Form.Check>
                                        <Form.Check.Input
                                            className={classNames(styles.checkboxLg, styles.pointer)}
                                            checked={selectedParts.some(p => p.id === part.id) || selectAll}
                                            onChange={() => handleCheckboxChange(part)}
                                            disabled={selectAll}
                                        />
                                    </Form.Check>
                                </td>
                                <td scope="row">
                                    <CategoryIcon categoryId={part.groupId} />
                                </td>
                                <td scope="row">{part.customTitle ? part.customTitle : part.title}</td>
                                <td scope="row">{part.markTitle}</td>
                                <td scope="row">{part.modelTitle}</td>
                                <td scope="row">
                                    <CellThumbCarousel pictures={part.images} part={part} />
                                </td>
                                <td scope="row" className={styles.default}>
                                    <CopyButton
                                        copyText={part.partNumber}
                                        overlayText={'ОЕМ скопирован в буфер обмена'}
                                        isText
                                    />
                                </td>
                                <td scope="row">
                                    <CellEditPrice id={part.id} price={part.price || 0} />
                                </td>
                                <td scope="row" className={styles.alignRight}>
                                    <div>
                                        <Badge text={formatDate(part.updatedAt)} color="#ddd" fontColor="#555" />
                                    </div>
                                    {+countDaysInStock(part.updatedAt) > 10 ? (
                                        <div>
                                            <Badge
                                                text={
                                                    countDaysInStock(part.updatedAt) +
                                                    ' ' +
                                                    num_word(+countDaysInStock(part.updatedAt), ['день', 'дня', 'дней'])
                                                }
                                                color="#eee"
                                                fontColor="#777"
                                            />
                                        </div>
                                    ) : (
                                        ''
                                    )}
                                </td>
                                <td>
                                    <div className="d-flex align-items-center" style={{ gap: 8 }}>
                                        <HoverOverlay tooltipText={'Редактировать'}>
                                            <Button
                                                variant="secondary"
                                                size="sm"
                                                onClick={() => openEditModal(part)}
                                                className="d-flex align-items-center"
                                            >
                                                <Pencil color={'white'} size={18} />
                                            </Button>
                                        </HoverOverlay>
                                        <HoverOverlay tooltipText={'Возврат'}>
                                            <Button
                                                variant="danger"
                                                size="sm"
                                                onClick={() => dispatch(setPartArchivedStatus(part.id, false))}
                                                className="d-flex align-items-center"
                                            >
                                                <ArrowLeft color={'white'} size={18} />
                                            </Button>
                                        </HoverOverlay>
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                )}
            </Table>
            {!isLoading && parts.length === 0 ? (
                <ListNoItems slice={'partList'} />
            ) : (
                <ListPagination
                    page={page}
                    setPage={page =>
                        dispatch({
                            type: storeDispatchTypes.setPageFilterParts,
                            value: { page },
                        })
                    }
                    perPage={perPage}
                    itemsCount={itemsCount}
                    justifyContent={'flex-end'}
                />
            )}
        </div>
    )
}

export default ArchivedPartList
